import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Search, Plus, Minus, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';
import { adminFetch } from '../../utils/adminAuth';
import { effectiveUnitPrice } from '../../utils/pricing';

const API_URL = '';

const fmt = (n) => new Intl.NumberFormat('fr-DZ').format(Math.round(n)) + ' DA';
const fmtTs = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('fr-DZ', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
};

export default function EditOrderItemsModal({ order, onClose, onSuccess }) {
  const [items, setItems] = useState(order?.items || []);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingOps, setPendingOps] = useState([]); // queue of operations to send

  // Fetch history
  useEffect(() => {
    if (!order?.order_id) return;
    adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${order.order_id}/history`)
      .then(r => r.ok ? r.json() : { history: [] })
      .then(d => setHistory(d.history || []));
  }, [order?.order_id]);

  // Search products with debounce
  useEffect(() => {
    if (!search.trim()) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`${API_URL}/api/products?search=${encodeURIComponent(search)}&limit=10`);
        const d = await r.json();
        setSearchResults(d.products || []);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const subtotal = useMemo(() =>
    items.reduce((sum, it) => sum + (it.price || 0) * (it.quantity || 0), 0)
  , [items]);

  const isFreeShipping = subtotal >= 10000;
  const baseShipping = order?.shipping_cost || 0;
  const estShipping = isFreeShipping ? 0 : baseShipping;
  const estTotal = subtotal + estShipping;

  const handleQtyChange = (idx, delta) => {
    setItems(prev => {
      const next = [...prev];
      const item = next[idx];
      const newQty = (item.quantity || 0) + delta;
      if (newQty <= 0) {
        next.splice(idx, 1);
        setPendingOps(ops => [...ops, { action: 'remove', cart_item_index: idx, _summary: `−1 ${item.name}` }]);
      } else {
        next[idx] = { ...item, quantity: newQty };
        setPendingOps(ops => [...ops, { action: 'update_qty', cart_item_index: idx, quantity: newQty, _summary: `${item.name} → ${newQty}` }]);
      }
      return next;
    });
  };

  const handleRemove = (idx) => {
    const item = items[idx];
    setItems(prev => prev.filter((_, i) => i !== idx));
    setPendingOps(ops => [...ops, { action: 'remove', cart_item_index: idx, _summary: `Supprimé: ${item.name}` }]);
  };

  const handleAdd = (product, size, color) => {
    const variant = (product.variants || []).find(v => v.size === size && (v.color || '') === (color || ''));
    if (!variant) { toast.error('Variante introuvable'); return; }
    if ((variant.stock || 0) < 1) { toast.error('Stock épuisé pour cette variante'); return; }
    const price = product.sale_price || product.price;
    const newItem = {
      product_id: product.product_id,
      name: product.name,
      price,
      size,
      color: color || '',
      quantity: 1,
      image_url: product.images?.[0]?.url || null,
    };
    setItems(prev => [...prev, newItem]);
    setPendingOps(ops => [...ops, { action: 'add', product_id: product.product_id, size, color: color || '', quantity: 1, _summary: `+ ${product.name} (${size}${color ? ` / ${color}` : ''})` }]);
    setSearch('');
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    if (pendingOps.length === 0) {
      toast.info('Aucune modification à enregistrer');
      return;
    }
    setSubmitting(true);
    try {
      const ops = pendingOps.map(({ _summary, ...rest }) => rest);
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/orders/${order.order_id}/items`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operations: ops }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Erreur lors de la modification');
      }
      toast.success(`Commande mise à jour — nouveau total : ${fmt(data.order.total)}`);
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error(e.message || 'Erreur réseau');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="edit-items-overlay">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-items-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#E0DFDD] sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-serif text-xl">Modifier la commande #{order.order_id}</h2>
            <p className="text-xs text-[#6B6A68] mt-1">{order.customer_name} • {order.wilaya} • Statut : <span className="font-medium">{order.order_status}</span></p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F5F4F2]" data-testid="close-edit-items"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* Items list */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Articles ({items.length})</h3>
            {items.length === 0 ? (
              <p className="text-sm text-red-600 bg-red-50 p-3 border border-red-200">Une commande ne peut pas être vide. Ajoute au moins un article ou utilise l'annulation.</p>
            ) : (
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 border border-[#E0DFDD]" data-testid={`edit-item-${idx}`}>
                    {it.image_url && <img src={it.image_url} alt={it.name} className="w-14 h-14 object-cover" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{it.name}</p>
                      <p className="text-xs text-[#6B6A68]">Taille : {it.size}{it.color ? ` • ${it.color}` : ''} • {fmt(it.price)} l'unité</p>
                    </div>
                    <div className="flex items-center border border-[#E0DFDD]">
                      <button onClick={() => handleQtyChange(idx, -1)} className="p-1.5 hover:bg-[#F5F4F2]" data-testid={`qty-decrease-${idx}`} disabled={submitting}><Minus size={14} /></button>
                      <span className="w-8 text-center text-sm font-medium">{it.quantity}</span>
                      <button onClick={() => handleQtyChange(idx, +1)} className="p-1.5 hover:bg-[#F5F4F2]" data-testid={`qty-increase-${idx}`} disabled={submitting}><Plus size={14} /></button>
                    </div>
                    <p className="font-medium text-sm w-24 text-right">{fmt(it.price * it.quantity)}</p>
                    <button onClick={() => handleRemove(idx)} className="p-1.5 text-red-600 hover:bg-red-50" data-testid={`remove-item-${idx}`} disabled={submitting}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add item */}
          <div>
            <h3 className="text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium mb-3">Ajouter un article</h3>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9E9C9A]" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un produit par nom..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#E0DFDD] focus:border-[#D4AF37] outline-none"
                data-testid="add-item-search"
              />
              {searching && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#9E9C9A]" />}
            </div>
            {searchResults.length > 0 && (
              <div className="mt-2 border border-[#E0DFDD] divide-y divide-[#E0DFDD] max-h-72 overflow-y-auto" data-testid="add-item-results">
                {searchResults.map(p => (
                  <ProductPicker key={p.product_id} product={p} onPick={(size, color) => handleAdd(p, size, color)} />
                ))}
              </div>
            )}
          </div>

          {/* Pending operations summary */}
          {pendingOps.length > 0 && (
            <div className="bg-[#FFFBEB] border border-[#D4AF37] p-3" data-testid="pending-ops">
              <p className="text-xs uppercase tracking-[0.15em] text-[#1A1514] font-medium mb-2">Modifications en attente ({pendingOps.length})</p>
              <ul className="text-xs text-[#1A1514] space-y-0.5">
                {pendingOps.map((op, i) => <li key={i}>• {op._summary}</li>)}
              </ul>
            </div>
          )}

          {/* Totals preview */}
          <div className="bg-[#F5F4F2] p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-[#6B6A68]">Sous-total</span><span>{fmt(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#6B6A68]">Livraison</span><span className={isFreeShipping ? 'text-[#A3B19B] font-medium' : ''}>{isFreeShipping ? 'GRATUITE' : fmt(estShipping)}</span></div>
            <div className="flex justify-between font-semibold pt-1.5 border-t border-[#E0DFDD]"><span>Nouveau total</span><span className="font-serif text-base" data-testid="edit-items-new-total">{fmt(estTotal)}</span></div>
            {order.total !== estTotal && (
              <p className="text-xs text-[#D4AF37] pt-1">
                Ancien total : {fmt(order.total)} → Nouveau : {fmt(estTotal)} ({estTotal > order.total ? '+' : ''}{fmt(estTotal - order.total)})
              </p>
            )}
          </div>

          {/* History */}
          {history.length > 0 && (
            <details className="border border-[#E0DFDD]">
              <summary className="cursor-pointer p-3 text-xs uppercase tracking-[0.15em] text-[#6B6A68] font-medium flex items-center gap-2 hover:bg-[#F5F4F2]">
                <History size={12} />
                Journal des modifications ({history.length})
              </summary>
              <ul className="divide-y divide-[#E0DFDD] max-h-60 overflow-y-auto" data-testid="edit-items-history">
                {history.slice().reverse().map((h, i) => (
                  <li key={i} className="p-3 text-xs">
                    <p>
                      <span className="font-medium">{h.action === 'add_item' ? 'Ajout' : h.action === 'remove_item' ? 'Suppression' : h.action === 'update_qty' ? 'Quantité' : h.action}</span>
                      <span className="text-[#6B6A68]"> — {fmtTs(h.ts)} • {h.by}</span>
                    </p>
                    <p className="text-[#6B6A68] mt-0.5">
                      {h.details?.name}
                      {h.details?.size ? ` (${h.details.size}${h.details?.color ? ` / ${h.details.color}` : ''})` : ''}
                      {' • '}
                      {h.action === 'update_qty'
                        ? `${h.details?.old_qty} → ${h.details?.new_qty}`
                        : `qté ${h.details?.quantity}`}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-[#E0DFDD] p-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#E0DFDD] hover:bg-[#F5F4F2]" disabled={submitting} data-testid="cancel-edit-items">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || pendingOps.length === 0 || items.length === 0}
            className="px-5 py-2 text-sm font-medium bg-[#1A1514] text-white hover:bg-[#2A2520] disabled:opacity-50 flex items-center gap-2"
            data-testid="submit-edit-items"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Enregistrer ({pendingOps.length})
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductPicker({ product, onPick }) {
  const variantsInStock = (product.variants || []).filter(v => (v.stock || 0) > 0);
  if (variantsInStock.length === 0) {
    return (
      <div className="p-2.5 flex items-center gap-3 opacity-50">
        {product.images?.[0]?.url && <img src={product.images[0].url} alt="" className="w-10 h-10 object-cover" />}
        <span className="text-sm flex-1">{product.name}</span>
        <span className="text-xs text-red-600">Rupture totale</span>
      </div>
    );
  }
  return (
    <div className="p-2.5">
      <div className="flex items-center gap-3 mb-2">
        {product.images?.[0]?.url && <img src={product.images[0].url} alt="" className="w-10 h-10 object-cover" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{product.name}</p>
          <p className="text-xs text-[#6B6A68]">{effectiveUnitPrice(product, null)} DA</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 ml-13">
        {variantsInStock.map((v, i) => (
          <button
            key={i}
            onClick={() => onPick(v.size, v.color || '')}
            className="px-2 py-1 text-[11px] border border-[#E0DFDD] hover:border-[#D4AF37] hover:bg-[#FFFBEB]"
            data-testid={`pick-variant-${product.product_id}-${v.size}`}
          >
            {v.size}{v.color ? ` / ${v.color}` : ''} <span className="text-[#9E9C9A]">({v.stock})</span>
          </button>
        ))}
      </div>
    </div>
  );
}
